import { Link } from "react-router-dom";
import { SEO } from "@/components/seo";
import {
  FiDollarSign,
  FiHome,
  FiPercent,
  FiArrowRight,
  FiTarget,
  FiRepeat,
  FiShield,
  FiPieChart,
} from "@/lib/icons";

interface CalculatorCard {
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  badge?: string;
}

const calculators: CalculatorCard[] = [
  {
    title: "Property Budget Calculator",
    description:
      "Find out what property you can afford based on your savings, income, and borrowing capacity. Factors in stamp duty, LMI, and other costs.",
    href: "/calculators/property-budget",
    icon: <FiHome className="size-6" />,
    badge: "Popular",
  },
  {
    title: "Borrowing Power Calculator",
    description:
      "Find out how much you could borrow based on your income, expenses, and financial commitments.",
    href: "/calculators/borrowing-power",
    icon: <FiDollarSign className="size-6" />,
  },
  {
    title: "Repayment Calculator",
    description:
      "Calculate your estimated weekly, fortnightly, or monthly repayments with amortization breakdown.",
    href: "/calculators/repayments",
    icon: <FiPercent className="size-6" />,
  },
  {
    title: "Extra Repayment Calculator",
    description:
      "See how much time and interest you could save by making extra repayments on your loan.",
    href: "/calculators/extra-repayments",
    icon: <FiTarget className="size-6" />,
  },
  {
    title: "Refinance Savings Calculator",
    description:
      "Compare your current loan to a new one and see how much you could save by refinancing.",
    href: "/calculators/refinance",
    icon: <FiRepeat className="size-6" />,
  },
  {
    title: "LMI Calculator",
    description:
      "Calculate Lenders Mortgage Insurance costs based on your deposit and property value.",
    href: "/calculators/lmi",
    icon: <FiShield className="size-6" />,
  },
];

// Stamp Duty Calculator positioned separately for center bottom placement
const stampDutyCalculator: CalculatorCard = {
  title: "Stamp Duty Calculator",
  description:
    "Calculate stamp duty, transfer fees, and First Home Owner Grant for property purchases in any Australian state.",
  href: "/calculators/stamp-duty",
  icon: <FiPieChart className="size-6" />,
};

/**
 * Calculator card component
 */
function CalculatorCardComponent({ calc }: { calc: CalculatorCard }) {
  return (
    <Link to={calc.href}>
      <div className="group relative bg-background rounded-2xl p-8 border border-border transition-all duration-300 h-full hover:shadow-lg hover:border-primary/20 cursor-pointer">
        {/* Badge */}
        {calc.badge && (
          <div className="absolute top-4 right-4">
            <span className="text-xs font-medium text-accent-foreground bg-accent px-3 py-1 rounded-full">
              {calc.badge}
            </span>
          </div>
        )}

        {/* Icon */}
        <div className="p-4 bg-cream rounded-xl inline-flex mb-6">
          {calc.icon}
        </div>

        {/* Content */}
        <h3 className="font-serif text-xl mb-3">{calc.title}</h3>
        <p className="text-muted-foreground text-sm mb-6">{calc.description}</p>

        {/* Action */}
        <span className="inline-flex items-center gap-2 text-sm font-medium text-primary group-hover:gap-3 transition-all">
          Calculate Now
          <FiArrowRight className="size-4" />
        </span>
      </div>
    </Link>
  );
}

/**
 * Calculators index page
 */
export function CalculatorsPage() {
  return (
    <>
      <SEO
        title="Financial Calculators"
        description="Use our free financial calculators to estimate stamp duty, borrowing power, loan repayments, and compare loan options across Australia."
        url="/calculators"
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6 text-center">
          <p className="section-label mb-4">Tools & Resources</p>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
            Financial Calculators
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Use our free calculators to help you understand the costs involved in
            buying property and find out how much you could borrow.
          </p>
        </div>
      </section>

      {/* Calculator Grid */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {calculators.map((calc) => (
              <CalculatorCardComponent key={calc.href} calc={calc} />
            ))}
          </div>
          {/* Stamp Duty Calculator - Centered at Bottom */}
          <div className="mt-6 flex justify-center">
            <div className="w-full md:w-1/2 lg:w-1/3">
              <CalculatorCardComponent calc={stampDutyCalculator} />
            </div>
          </div>
        </div>
      </section>

      {/* Help Section */}
      <section className="bg-primary text-primary-foreground py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <p className="section-label mb-4">
            Need Help?
          </p>
          <h2 className="font-serif text-3xl md:text-4xl mb-6">
            These calculators are just the start
          </h2>
          <p className="text-primary-foreground/90 mb-8 max-w-2xl mx-auto">
            For a complete assessment of your borrowing capacity and to explore
            loan options tailored to your situation, get in touch with our team
            for a free consultation.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-6 py-3 rounded-full font-medium hover:bg-accent/90 transition-colors"
          >
            Book a Consultation
            <FiArrowRight className="size-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
